import React, { useCallback, useEffect, useMemo, useState } from "react";
import { authFetch } from "../utils/authFetch";

const EMPTY = {
  title: "",
  description: "",
  benefit_type: "PERCENT",
  discount_value: "",
  gift_product: "",
  gift_quantity: "1",
  audience: "ALL",
  selected_companies: [],
  minimum_order: "0",
  max_uses_per_company: "",
  start_date: new Date().toLocaleDateString("en-CA"),
  end_date: "",
  active: false,
};

const benefitLabels = {
  PERCENT: "Sconto percentuale",
  FIXED: "Sconto in euro",
  GIFT: "Prodotto omaggio",
  INFO: "Promozione informativa",
};

const audienceLabels = {
  ALL: "Tutte le aziende",
  NEW: "Nuovi clienti",
  SELECTED: "Aziende selezionate",
};

const styles = {
  page: { padding: 24, maxWidth: 1250, margin: "0 auto", color: "#f5f0e8" },
  panel: { background: "#21191c", border: "1px solid #705342", borderRadius: 14, padding: 20, marginBottom: 18 },
  input: { width: "100%", padding: 11, background: "#fff", color: "#222", border: "1px solid #ccc", borderRadius: 8, boxSizing: "border-box" },
  button: { padding: "11px 16px", background: "#b78c43", color: "#171010", border: 0, borderRadius: 8, cursor: "pointer", fontWeight: 700 },
  secondary: { padding: "10px 14px", background: "#47343a", color: "#fff", border: "1px solid #856c59", borderRadius: 8, cursor: "pointer" },
  label: { display: "block", marginBottom: 6, fontWeight: 600 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 16 },
};

function Field({ label, children }) {
  return <label style={{ display: "block" }}><span style={styles.label}>{label}</span>{children}</label>;
}

function formatError(data) {
  if (typeof data?.detail === "string") return data.detail;
  if (!data || typeof data !== "object") return "Operazione non riuscita.";
  return Object.entries(data).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`).join(" | ");
}

export default function B2BPromotionsPage() {
  const [promotions, setPromotions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ ...EMPTY });
  const [editingId, setEditingId] = useState(null);
  const [image, setImage] = useState(null);
  const [search, setSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const responses = await Promise.all([
        authFetch("/api/b2b/promotions/"),
        authFetch("/api/aziende/"),
        authFetch("/api/prodotti/?scope=catalogo&tipo_listino=azienda"),
      ]);
      for (const response of responses) {
        if (!response.ok) throw new Error(`Caricamento non riuscito (HTTP ${response.status}).`);
      }
      const [p, c, pr] = await Promise.all(responses.map((r) => r.json()));
      setPromotions(Array.isArray(p) ? p : p.results || []);
      setCompanies(Array.isArray(c) ? c : c.results || []);
      setProducts(Array.isArray(pr) ? pr : pr.results || []);
    } catch (err) {
      setError(err.message || "Errore durante il caricamento.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (key, value) => setForm((old) => ({ ...old, [key]: value }));

  const reset = () => {
    setEditingId(null);
    setForm({ ...EMPTY, selected_companies: [] });
    setImage(null);
    setCompanySearch("");
    setError("");
    setNotice("");
  };

  const edit = (promotion) => {
    setEditingId(promotion.id);
    setForm({
      ...EMPTY,
      ...promotion,
      gift_product: promotion.gift_product || "",
      discount_value: promotion.discount_value ?? "",
      end_date: promotion.end_date || "",
      max_uses_per_company: promotion.max_uses_per_company ?? "",
      selected_companies: promotion.selected_companies || [],
    });
    setImage(null);
    setError("");
    setNotice("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    if (form.audience === "SELECTED" && !form.selected_companies.length) {
      setError("Seleziona almeno un'azienda.");
      return;
    }
    setSaving(true);
    try {
      const values = {
        title: form.title,
        description: form.description || "",
        benefit_type: form.benefit_type,
        discount_value: ["PERCENT", "FIXED"].includes(form.benefit_type)
          ? form.discount_value : "0",
        gift_product: form.benefit_type === "GIFT"
          ? (form.gift_product || null) : null,
        gift_quantity: form.gift_quantity || "1",
        audience: form.audience,
        selected_companies: form.audience === "SELECTED"
          ? form.selected_companies : [],
        minimum_order: form.minimum_order || "0",
        max_uses_per_company: form.max_uses_per_company || null,
        start_date: form.start_date,
        end_date: form.end_date || null,
        active: Boolean(form.active),
      };

      const url = editingId
        ? `/api/b2b/promotions/${editingId}/`
        : "/api/b2b/promotions/";

      let body;
      let headers = {};

      if (image) {
        const payload = new FormData();

        Object.entries(values).forEach(([key, value]) => {
          if (key === "selected_companies") {
            value.forEach((id) => payload.append(key, String(id)));
          } else if (value === null) {
            payload.append(key, "");
          } else {
            payload.append(key, String(value));
          }
        });

        if (values.selected_companies.length === 0) {
          payload.append("clear_selected_companies", "1");
        }

        payload.append("image", image);
        body = payload;
      } else {
        body = JSON.stringify(values);
        headers = { "Content-Type": "application/json" };
      }

      const response = await authFetch(url, {
        method: editingId ? "PATCH" : "POST",
        headers,
        body,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(formatError(data));

      reset();
      setNotice(editingId ? "Promozione aggiornata." : "Promozione creata.");
      await load();
    } catch (err) {
      setError(err.message || "Salvataggio non riuscito.");
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (promotion) => {
    if (!window.confirm(`Disattivare "${promotion.title}"?`)) return;
    setError("");
    try {
      const response = await authFetch(`/api/b2b/promotions/${promotion.id}/`, { method: "DELETE" });
      if (!response.ok) throw new Error(`Disattivazione non riuscita (HTTP ${response.status}).`);
      setNotice("Promozione disattivata.");
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const filtered = useMemo(() => promotions.filter((p) =>
    `${p.title} ${p.description}`.toLowerCase().includes(search.toLowerCase())
  ), [promotions, search]);

  const matchingCompanies = companies.filter((c) =>
    `${c.ragione_sociale || c.nome || c.name || ""} ${c.id}`.toLowerCase()
      .includes(companySearch.toLowerCase())
  );

  return (
    <main style={styles.page}>
      <h1 style={{ color: "#e3bd77" }}>Promozioni B2B</h1>
      <p>Gestione delle campagne riservate alle aziende. Le offerte per i clienti privati rimangono separate.</p>

      {error && <div role="alert" style={{ ...styles.panel, borderColor: "#e57373", color: "#ffb6b6" }}>{error}</div>}
      {notice && <div role="status" style={{ ...styles.panel, borderColor: "#80b88b" }}>{notice}</div>}

      <section style={styles.panel}>
        <h2>{editingId ? `Modifica promozione #${editingId}` : "Nuova promozione"}</h2>
        <form onSubmit={submit}>
          <div style={styles.grid}>
            <Field label="Titolo *">
              <input style={styles.input} required maxLength={160} value={form.title} onChange={(e) => update("title", e.target.value)} />
            </Field>
            <Field label="Tipo di promozione">
              <select style={styles.input} value={form.benefit_type} onChange={(e) => update("benefit_type", e.target.value)}>
                {Object.entries(benefitLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </select>
            </Field>
            <Field label="Destinatari">
              <select style={styles.input} value={form.audience} onChange={(e) => update("audience", e.target.value)}>
                {Object.entries(audienceLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </select>
            </Field>
            <Field label="Immagine">
              <input style={styles.input} type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} />
            </Field>
            {(form.benefit_type === "PERCENT" || form.benefit_type === "FIXED") && (
              <Field label={form.benefit_type === "PERCENT" ? "Sconto (%) *" : "Sconto (€) *"}>
                <input style={styles.input} type="number" min="0.01" max={form.benefit_type === "PERCENT" ? "100" : undefined} step="0.01" required value={form.discount_value} onChange={(e) => update("discount_value", e.target.value)} />
              </Field>
            )}
            {form.benefit_type === "GIFT" && (
              <>
                <Field label="Prodotto omaggio *">
                  <select style={styles.input} required value={form.gift_product} onChange={(e) => update("gift_product", e.target.value)}>
                    <option value="">Seleziona un prodotto</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.nome || p.name || p.title || `Prodotto #${p.id}`}</option>)}
                  </select>
                </Field>
                <Field label="Quantità omaggio *">
                  <input style={styles.input} type="number" min="0.01" step="0.01" required value={form.gift_quantity} onChange={(e) => update("gift_quantity", e.target.value)} />
                </Field>
              </>
            )}
            <Field label="Ordine minimo (€)">
              <input style={styles.input} type="number" min="0" step="0.01" value={form.minimum_order} onChange={(e) => update("minimum_order", e.target.value)} />
            </Field>
            <Field label="Massimo utilizzi per azienda (vuoto = illimitato)">
              <input style={styles.input} type="number" min="1" step="1" value={form.max_uses_per_company} onChange={(e) => update("max_uses_per_company", e.target.value)} />
            </Field>
            <Field label="Data inizio *">
              <input style={styles.input} type="date" required value={form.start_date} onChange={(e) => update("start_date", e.target.value)} />
            </Field>
            <Field label="Data fine (facoltativa)">
              <input style={styles.input} type="date" min={form.start_date} value={form.end_date} onChange={(e) => update("end_date", e.target.value)} />
            </Field>
          </div>

          <div style={{ marginTop: 16 }}>
            <Field label="Descrizione">
              <textarea style={{ ...styles.input, minHeight: 100 }} value={form.description} onChange={(e) => update("description", e.target.value)} />
            </Field>
          </div>

          {form.audience === "NEW" && (
            <p style={{ color: "#e3bd77" }}>Le campagne per nuovi clienti saranno visibili quando sarà completata la verifica automatica dell'idoneità.</p>
          )}

          {form.audience === "SELECTED" && (
            <div style={{ marginTop: 18 }}>
              <h3>Aziende selezionate ({form.selected_companies.length})</h3>
              <input style={styles.input} placeholder="Cerca azienda..." value={companySearch} onChange={(e) => setCompanySearch(e.target.value)} />
              <div style={{ maxHeight: 220, overflowY: "auto", marginTop: 10 }}>
                {matchingCompanies.map((c) => (
                  <label key={c.id} style={{ display: "block", padding: 7 }}>
                    <input type="checkbox" checked={form.selected_companies.map(String).includes(String(c.id))}
                      onChange={(e) => update("selected_companies", e.target.checked
                        ? [...form.selected_companies, c.id]
                        : form.selected_companies.filter((id) => String(id) !== String(c.id)))} />
                    {" "}{c.ragione_sociale || c.nome || c.name || `Azienda #${c.id}`}
                  </label>
                ))}
              </div>
            </div>
          )}

          <label style={{ display: "block", margin: "20px 0" }}>
            <input type="checkbox" checked={Boolean(form.active)} onChange={(e) => update("active", e.target.checked)} />
            {" "}Promozione attiva
          </label>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button style={styles.button} type="submit" disabled={saving}>{saving ? "Salvataggio..." : editingId ? "Salva modifiche" : "Crea promozione"}</button>
            <button style={styles.secondary} type="button" onClick={reset}>Annulla / Nuova</button>
          </div>
        </form>
      </section>

      <section style={styles.panel}>
        <h2>Campagne registrate ({promotions.length})</h2>
        <input style={styles.input} placeholder="Cerca una promozione..." value={search} onChange={(e) => setSearch(e.target.value)} />
        {loading ? <p>Caricamento...</p> : filtered.length === 0 ? <p>Nessuna promozione trovata.</p> : (
          <div style={{ display: "grid", gap: 14, marginTop: 18 }}>
            {filtered.map((p) => (
              <article key={p.id} style={{ border: "1px solid #60474a", borderRadius: 10, padding: 15 }}>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  {p.image_url && <img src={p.image_url} alt="" style={{ width: 110, height: 90, objectFit: "cover", borderRadius: 8 }} />}
                  <div style={{ flex: 1 }}>
                    <h3 style={{ marginTop: 0 }}>{p.title}</h3>
                    <p>{benefitLabels[p.benefit_type]} · {audienceLabels[p.audience]}</p>
                    <p>{p.start_date} — {p.end_date || "senza scadenza"}</p>
                    <strong style={{ color: p.active ? "#9cdda8" : "#e3bd77" }}>{p.active ? "Attiva" : "Disattivata"}</strong>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "start" }}>
                    <button style={styles.secondary} onClick={() => edit(p)}>Modifica</button>
                    {p.active && <button style={styles.secondary} onClick={() => deactivate(p)}>Disattiva</button>}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
